import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listTodos, TodoItem, createTodo, deleteTodo, updateTodo } from '../lib/todos';
import { User } from '@supabase/supabase-js';
import { useGroupContext } from '../lib/groups/context';
import { supabase } from '../lib/supabase';
import { getClaimedContactId } from '../lib/profiles';
import { listGroupMemberContactsCore, listContactsCore } from '@gv/core';
import { ChevronLeft, ChevronRight, User as UserIcon, Plus, SlidersHorizontal, X, Menu, CheckSquare, RefreshCcw, ChevronDown, ChevronRight as ChevronRightIcon, Search } from 'lucide-react';
import WeekView from './WeekView';
import DayViewModal from './DayViewModal';
import MonthView from './MonthView';
import DayView from './DayView';
import YearView from './YearView';
import EventDetailsModal from './EventDetailsModal';
import TranscriptView from './TranscriptView';
import { getEventById } from '../lib/events';
import CalendarManager from './CalendarManager';
import MiniMonthPicker from './MiniMonthPicker';
import UndoToast from './UndoToast';
import ContactFirstEventModal from './ContactFirstEventModal';
import ContactTimelineModal from './ContactTimelineModal';

export default function NewCalendarView({ user }: { user: User }) {
    const { activeGroupId } = useGroupContext();

    // Core state
    const [items, setItems] = useState<TodoItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // View state  
    const [view, setView] = useState<'day' | 'week' | 'month' | 'year'>('week'); // Default to week like Google Calendar
    const [currentWeekDate, setCurrentWeekDate] = useState<Date>(() => new Date());
    const [currentMonthDate, setCurrentMonthDate] = useState<Date>(() => new Date());
    const [currentDayDate, setCurrentDayDate] = useState<Date>(() => new Date());
    const [currentYearDate, setCurrentYearDate] = useState<Date>(() => new Date());
    const [dayModal, setDayModal] = useState<{ open: boolean; date: Date | null }>({ open: false, date: null });

    // Calendar management
    const [calendars, setCalendars] = useState<any[]>([]);
    const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);

    // Filter state
    const [mode, setMode] = useState<'mine' | 'all' | 'member'>('mine');
    const [selectedMemberId, setSelectedMemberId] = useState<string>('');
    const [selectedPersonId, setSelectedPersonId] = useState<string>('');
    const [personQuery, setPersonQuery] = useState('');
    const [personOptions, setPersonOptions] = useState<Array<{ id: string; name: string; avatar?: string | null }>>([]);
    const [personFocused, setPersonFocused] = useState(false);

    // Modal state
    const [eventModalOpen, setEventModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<TodoItem | null>(null);
    const [eventModalDefaults, setEventModalDefaults] = useState<{ date?: string; time?: string }>({});
    const [eventModalViewOnly, setEventModalViewOnly] = useState<boolean>(false);

    // Transcript chat view state
    const [transcriptChatOpen, setTranscriptChatOpen] = useState(false);
    const [transcriptEvent, setTranscriptEvent] = useState<TodoItem | null>(null);

    const [isMobile, setIsMobile] = useState<boolean>(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Contact-first event modal
    const [contactFirstModalOpen, setContactFirstModalOpen] = useState(false);

    // Contact timeline modal
    const [timelineModalOpen, setTimelineModalOpen] = useState(false);
    // Mobile drawers
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [tasksOpen, setTasksOpen] = useState(false);
    const [tasks, setTasks] = useState<TodoItem[]>([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [filtersCollapsed, setFiltersCollapsed] = useState(true);

    // Header interactions
    const [monthPanelOpen, setMonthPanelOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Quick Add minimal modal
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const [quickAddType, setQuickAddType] = useState<'event' | 'task'>('event');
    const [quickAddTitle, setQuickAddTitle] = useState('');
    const [quickAddAllDay, setQuickAddAllDay] = useState(true);
    const [quickAddDate, setQuickAddDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
    const [quickAddTime, setQuickAddTime] = useState<string>('');
    const [quickAddSaving, setQuickAddSaving] = useState(false);

    // Mobile drawer slide animation state
    const [mobileMenuSlide, setMobileMenuSlide] = useState(false);

    const saveQuickAdd = async () => {
        if (!quickAddTitle.trim()) return;
        try {
            setQuickAddSaving(true);
            let dueISO: string | null = null;
            if (quickAddDate) {
                if (quickAddAllDay || !quickAddTime) {
                    // All-day: set to midnight local, store as ISO (UTC)
                    const d = new Date(`${quickAddDate}T00:00`);
                    dueISO = d.toISOString();
                } else {
                    const d = new Date(`${quickAddDate}T${quickAddTime}`);
                    dueISO = d.toISOString();
                }
            }
            const saved = await createTodo({
                title: quickAddTitle.trim(),
                due_date: dueISO,
                done: false,
                contact_id: selectedPersonId || null,
                group_id: activeGroupId || null
            } as any, user.id);
            setItems(prev => [...prev, saved]);
            setQuickAddOpen(false);
            setQuickAddTitle('');
            setQuickAddAllDay(true);
            setQuickAddDate(new Date().toISOString().slice(0, 10));
            setQuickAddTime('');
        } finally {
            setQuickAddSaving(false);
        }
    };

    // Load tasks when drawer opens
    useEffect(() => {
        const load = async () => {
            try {
                setTasksLoading(true);
                const rows = await listTodos(user.id, { includeCompleted: false, groupId: activeGroupId || null });
                setTasks(rows);
            } catch {
                setTasks([]);
            } finally {
                setTasksLoading(false);
            }
        };
        if (tasksOpen) load();
    }, [tasksOpen, user?.id, activeGroupId]);

    // Animate mobile drawer in when opened; reset slide state when closed
    useEffect(() => {
        if (mobileMenuOpen) {
            const t = setTimeout(() => setMobileMenuSlide(true), 0);
            // Close on Escape
            const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMobileMenuSlide(false); setTimeout(() => setMobileMenuOpen(false), 200); } };
            window.addEventListener('keydown', onKey);
            return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
        } else {
            setMobileMenuSlide(false);
        }
    }, [mobileMenuOpen]);

    const closeMobileMenuAnimated = () => {
        setMobileMenuSlide(false);
        setTimeout(() => setMobileMenuOpen(false), 200);
    };

    // Search: compute filtered items for display
    const filteredItems = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter(it => (it.title || '').toLowerCase().includes(q));
    }, [items, searchQuery]);

    // Member management
    const [members, setMembers] = useState<Array<{ contact_id: string; name: string }>>([]);
    const [myContactId, setMyContactId] = useState<string | null>(null);

    // Undo system
    const [pendingUndo, setPendingUndo] = useState<null | { id: string; kind: 'delete' | 'cancel'; until: number; message: string; onUndo: () => Promise<void> }>(null);
    const [undoSeconds, setUndoSeconds] = useState(0);
    const undoTimerRef = useRef<number | null>(null);

    // Helper functions
    const startOfWeek = (date: Date): Date => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day;
        return new Date(d.setDate(diff));
    };

    const endOfWeek = (date: Date): Date => {
        const start = startOfWeek(date);
        const result = new Date(start);
        result.setDate(start.getDate() + 6);
        result.setHours(23, 59, 59, 999);
        return result;
    };

    const addWeeks = (date: Date, weeks: number): Date => {
        const result = new Date(date);
        result.setDate(result.getDate() + (weeks * 7));
        return result;
    };

    const getWeekLabel = (date: Date): string => {
        const start = startOfWeek(date);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        if (start.getMonth() === end.getMonth()) {
            return `${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('en-US', { month: 'short' })} - ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
    };
    const addDays = (date: Date, days: number): Date => { const r = new Date(date); r.setDate(r.getDate() + days); return r; };
    const addMonths = (date: Date, months: number): Date => { const r = new Date(date); r.setMonth(r.getMonth() + months); return r; };
    const addYears = (date: Date, years: number): Date => { const r = new Date(date); r.setFullYear(r.getFullYear() + years); return r; };

    // Load my claimed contact id and run backfill
    useEffect(() => {
        (async () => {
            try {
                const cid = await getClaimedContactId(user.id);
                setMyContactId(cid);

                // Force run backfill to ensure existing events have follow-ups
                const { backfillTodosForUser } = await import('../lib/backfill');
                await backfillTodosForUser(user.id);
            } catch { setMyContactId(null); }
        })();
    }, [user?.id]);

    // Debounced contact search
    useEffect(() => {
        let alive = true;
        const q = personQuery.trim();
        if (!q) { setPersonOptions([]); return; }
        const t = setTimeout(async () => {
            try {
                const rows = await listContactsCore(supabase, { query: q, limit: 6, includeArchived: false });
                if (!alive) return;
                const mapped = (rows || []).map((c: any) => ({ id: c.id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown', avatar: c.profile_image_url || c.photo || null }));
                setPersonOptions(mapped);
            } catch { if (alive) setPersonOptions([]); }
        }, 200);
        return () => { alive = false; clearTimeout(t); };
    }, [personQuery]);

    // Load group members
    useEffect(() => {
        (async () => {
            try {
                if (!activeGroupId) { setMembers([]); return; }
                const opts = await listGroupMemberContactsCore(supabase, activeGroupId);
                setMembers(opts);
            } catch { setMembers([]); }
        })();
    }, [activeGroupId]);

    // Load items for visible date range
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const assignedContactId = selectedPersonId ? null : (mode === 'mine' ? myContactId : (mode === 'member' ? (selectedMemberId || null) : null));

                // IMPROVED: If we're in "mine" mode but don't have a "My Contact" set,
                // fall back to showing all user's todos instead of nothing
                const effectiveAssignedContactId = mode === 'mine' && !myContactId ? null : assignedContactId;

                // Calculate date range based on current view
                let from: Date;
                let to: Date;
                if (view === 'week') {
                    const weekStart = startOfWeek(currentWeekDate);
                    const weekEnd = endOfWeek(currentWeekDate);
                    from = new Date(weekStart); from.setHours(0, 0, 0, 0);
                    to = new Date(weekEnd); to.setHours(23, 59, 59, 999);
                } else if (view === 'month') {
                    const first = new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth(), 1);
                    const last = new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() + 1, 0);
                    from = new Date(first); from.setHours(0, 0, 0, 0);
                    to = new Date(last); to.setHours(23, 59, 59, 999);
                } else if (view === 'day') {
                    const d = new Date(currentDayDate);
                    from = new Date(d); from.setHours(0, 0, 0, 0);
                    to = new Date(d); to.setHours(23, 59, 59, 999);
                } else {
                    // year view - expand to show more data
                    const y = currentYearDate.getFullYear();
                    const first = new Date(y - 1, 0, 1); // Include previous year
                    const last = new Date(y + 1, 11, 31); // Include next year
                    from = new Date(first); from.setHours(0, 0, 0, 0);
                    to = new Date(last); to.setHours(23, 59, 59, 999);
                }

                console.log(`🔍 Calendar date range: ${from.toISOString()} to ${to.toISOString()}`);

                const listOpts = {
                    includeCompleted: false,
                    groupId: activeGroupId || null,
                    assignedContactId: effectiveAssignedContactId,
                    contactId: selectedPersonId || null,
                    from: from.toISOString(),
                    to: to.toISOString()
                } as const;
                console.log('🧪 listTodos inputs:', { userId: user.id, listOpts });
                const rows = await listTodos(user.id, listOpts as any);

                // DEBUG: Log what we're loading to help diagnose issues
                console.log(`🔍 Calendar loading todos:`, {
                    mode,
                    assignedContactId,
                    effectiveAssignedContactId,
                    myContactId,
                    selectedPersonId,
                    activeGroupId,
                    dateRange: { from: from.toISOString(), to: to.toISOString() },
                    foundItems: rows.length
                });

                if (rows.length > 0) {
                    console.log('📋 Found todos:', rows.map(t => ({
                        title: t.title,
                        due_date: t.due_date,
                        event_id: t.event_id,
                        contact_id: t.contact_id,
                        assigned_contact_id: t.assigned_contact_id,
                        done: t.done
                    })));
                } else {
                    console.log('⚠️ No todos found! This might be why follow-ups aren\'t showing up.');
                    console.log('💡 Try:');
                    console.log('  1. Check if "My Contact" is set in Settings');
                    console.log('  2. Switch calendar to "All" mode instead of "Mine"');
                    console.log('  3. Check the date range - are your follow-ups within the visible period?');
                    if (mode === 'mine' && !myContactId) {
                        console.log('  4. ⚠️  "My Contact" is not set - showing all your todos instead');
                    }
                }

                setItems(rows);
            } catch (e: any) {
                console.error('❌ Calendar load failed:', e?.message || e, e);
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [user?.id, activeGroupId, mode, selectedMemberId, selectedPersonId, currentWeekDate, currentMonthDate, currentDayDate, currentYearDate, view, myContactId]);

    // Undo countdown logic
    useEffect(() => {
        if (!pendingUndo) return;
        const tick = () => {
            const remainingMs = Math.max(0, pendingUndo.until - Date.now());
            setUndoSeconds(Math.ceil(remainingMs / 1000));
            if (remainingMs <= 0) {
                setPendingUndo(null);
                if (undoTimerRef.current) window.clearInterval(undoTimerRef.current);
                undoTimerRef.current = null;
            }
        };
        tick();
        undoTimerRef.current = window.setInterval(tick, 250) as any;
        return () => { if (undoTimerRef.current) { window.clearInterval(undoTimerRef.current); undoTimerRef.current = null; } };
    }, [pendingUndo]);

    // Event handlers
    const closeEventModal = () => {
        setEventModalOpen(false);
        setSelectedEvent(null);
        setEventModalDefaults({});
        setEventModalViewOnly(false);
    };

    const handleEventClick = async (id: string) => {
        // Toggle behavior: if clicking the same event while details are visible, close it
        if (selectedEvent?.id === id && eventModalOpen) { closeEventModal(); return; }

        const event = items.find(item => item.id === id);
        if (event) {
            // Check if this is a transcript event by looking for transcript markers in title or notes
            const isTranscript = event.title?.toLowerCase().includes('transcript') ||
                event.title?.toLowerCase().includes('call transcript') ||
                (event.notes && event.notes.includes(':')) && // Contains speaker format like "Name: text"
                (event.notes.split('\n').some(line => /^[^:]+:\s*/.test(line.trim())));

            if (isTranscript) {
                // Hydrate with contact_event if available via event.event_id
                try {
                    let hydrated = event as any;
                    const backingId = (event as any).event_id as string | null | undefined;
                    if (backingId) {
                        const ce = await getEventById(backingId);
                        if (ce) {
                            // Merge core event fields into the todo-shaped object expected by TranscriptView
                            hydrated = {
                                ...event,
                                id: ce.id || event.id,
                                title: ce.name || event.title,
                                description: (event as any).description || ce.notes || '',
                                notes: ce.notes || (event as any).notes || '',
                                due_date: (event as any).due_date || ce.when || null,
                                participants: ce.participants,
                                customFields: ce.customFields,
                            };
                        }
                    }
                    setTranscriptEvent(hydrated);
                    setTranscriptChatOpen(true);
                } catch (e) {
                    console.error('Failed to hydrate transcript event from contact_events, opening raw todo object:', e);
                    setTranscriptEvent(event);
                    setTranscriptChatOpen(true);
                }
            } else {
                // Open regular event modal
                setSelectedEvent(event);
                setEventModalViewOnly(true); // open as view-only first
                setEventModalOpen(true);
            }
        }
    };

    const handleTimeSlotClick = (date: Date, hour: number) => {
        const dateStr = date.toISOString().split('T')[0];
        const timeStr = `${hour.toString().padStart(2, '0')}:00`;

        setSelectedEvent(null);
        setEventModalDefaults({ date: dateStr, time: timeStr });
        setEventModalViewOnly(false);
        setEventModalOpen(true);
    };

    const handleEventSave = (event: TodoItem) => {
        if (selectedEvent) {
            setItems(prev => prev.map(item => item.id === event.id ? event : item));
        } else {
            setItems(prev => [...prev, event]);
        }
        setEventModalOpen(false);
        setSelectedEvent(null);
        setEventModalDefaults({});
        setEventModalViewOnly(false);
    };

    const handleEventDelete = (id: string) => {
        setItems(prev => prev.filter(item => item.id !== id));
    };

    const handleCalendarToggle = (calendarId: string) => {
        setSelectedCalendars(prev =>
            prev.includes(calendarId)
                ? prev.filter(id => id !== calendarId)
                : [...prev, calendarId]
        );
    };

    const openNewEventModal = () => {
        setSelectedEvent(null);
        setEventModalDefaults({});
        setEventModalOpen(true);
    };

    const openContactFirstModal = () => {
        setContactFirstModalOpen(true);
    };

    return (
        <div className="w-full flex gap-4">
            {/* Sidebar with Calendar Manager */}
            {/* Sidebar: hidden on small screens */}
            <div className="hidden md:block w-64 flex-shrink-0 space-y-4 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto">
                <MiniMonthPicker
                    date={currentDayDate}
                    onChange={(d) => {
                        setCurrentDayDate(d);
                        if (view === 'week') setCurrentWeekDate(d);
                        else if (view === 'month') setCurrentMonthDate(new Date(d.getFullYear(), d.getMonth(), 1));
                        else if (view === 'year') setCurrentYearDate(new Date(d.getFullYear(), 0, 1));
                    }}
                />
                <CalendarManager
                    user={user}
                    selectedCalendars={selectedCalendars}
                    onCalendarToggle={handleCalendarToggle}
                    onCalendarsChange={setCalendars}
                />

                {/* Quick Actions */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-xl p-4 space-y-3 shadow-lg">
                    <div className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-3">Quick Actions</div>

                    <button
                        onClick={openContactFirstModal}
                        className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-4 py-3 rounded-lg shadow-md hover:shadow-lg font-medium transition-all duration-200 transform hover:scale-[1.02]"
                    >
                        <UserIcon className="w-5 h-5" />
                        <span>Quick Event</span>
                    </button>

                    <button
                        onClick={openNewEventModal}
                        className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 text-gray-100 px-4 py-3 rounded-lg shadow-md hover:shadow-lg font-medium transition-all duration-200 transform hover:scale-[1.02] border border-gray-600"
                    >
                        <Plus className="w-5 h-5" />
                        <span>New Event</span>
                    </button>
                </div>

                {/* Filters removed per product direction; filtering happens via calendars */}
            </div>

            {/* Main Calendar */}
            <div className={`flex-1 overflow-x-hidden ${isMobile && eventModalOpen ? 'pb-24' : ''}`}>
                {/* Controls */}
                <div className="bg-gray-900/95 backdrop-blur-sm border border-gray-800 rounded-lg p-3 mb-4 sticky top-0 z-30">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {/* Hamburger (mobile) */}
                            <button
                                onClick={() => setMobileMenuOpen(true)}
                                className="md:hidden p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700"
                                aria-label="Open menu"
                            >
                                <Menu className="w-4 h-4" />
                            </button>
                            {/* Logo (requested in hamburger header) */}
                            <img
                                src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                                alt="Vault Logo"
                                className="md:hidden h-4 w-4 object-contain"
                            />
                            {/* Month label: mobile = clickable dropdown; desktop = static Month YYYY */}
                            <button
                                onClick={() => setMonthPanelOpen(v => !v)}
                                className="md:hidden mx-1 px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white font-semibold flex items-center gap-1"
                                aria-expanded={monthPanelOpen}
                                aria-label="Select month"
                            >
                                <span className="truncate">
                                    {(view === 'week') && currentWeekDate.toLocaleDateString('en-US', { month: 'long' })}
                                    {(view === 'month') && currentMonthDate.toLocaleDateString('en-US', { month: 'long' })}
                                    {(view === 'day') && currentDayDate.toLocaleDateString('en-US', { month: 'long' })}
                                    {(view === 'year') && currentYearDate}
                                </span>
                                <ChevronDown className={`w-4 h-4 transition-transform ${monthPanelOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <div className="hidden md:block mx-2 text-white font-semibold truncate">
                                {(() => {
                                    const label =
                                        view === 'week' ? currentWeekDate :
                                            view === 'month' ? currentMonthDate :
                                                view === 'day' ? currentDayDate : new Date(currentYearDate, 0, 1);
                                    return label.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                                })()}
                            </div>

                            {/* Today: mobile numeric circle, desktop 'Today' pill */}
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    if (view === 'week') setCurrentWeekDate(now);
                                    else if (view === 'month') setCurrentMonthDate(now);
                                    else if (view === 'day') setCurrentDayDate(now);
                                    else setCurrentYearDate(now);
                                }}
                                className="md:hidden w-8 h-8 rounded-full border border-gray-700 text-gray-200 flex items-center justify-center text-sm"
                                title="Jump to today"
                                aria-label="Today"
                            >
                                {new Date().getDate()}
                            </button>

                            {/* Mobile navigation arrows */}
                            <div className="md:hidden flex items-center border border-gray-700 rounded-md overflow-hidden">
                                <button
                                    onClick={() => {
                                        if (view === 'week') setCurrentWeekDate(addWeeks(currentWeekDate, -1));
                                        else if (view === 'month') setCurrentMonthDate(addMonths(currentMonthDate, -1));
                                        else if (view === 'day') setCurrentDayDate(addDays(currentDayDate, -1));
                                        else setCurrentYearDate(addYears(currentYearDate, -1));
                                    }}
                                    className="p-1.5 text-gray-300 hover:bg-gray-700"
                                    title={`Previous ${view}`}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => {
                                        if (view === 'week') setCurrentWeekDate(addWeeks(currentWeekDate, 1));
                                        else if (view === 'month') setCurrentMonthDate(addMonths(currentMonthDate, 1));
                                        else if (view === 'day') setCurrentDayDate(addDays(currentDayDate, 1));
                                        else setCurrentYearDate(addYears(currentYearDate, 1));
                                    }}
                                    className="p-1.5 text-gray-300 hover:bg-gray-700 border-l border-gray-700"
                                    title={`Next ${view}`}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    if (view === 'week') setCurrentWeekDate(now);
                                    else if (view === 'month') setCurrentMonthDate(now);
                                    else if (view === 'day') setCurrentDayDate(now);
                                    else setCurrentYearDate(now);
                                }}
                                className="hidden md:inline-flex px-3 py-1.5 rounded-md border border-gray-700 text-gray-200 text-sm hover:bg-gray-700"
                            >
                                Today
                            </button>

                            {/* Navigation arrows */}
                            <div className="hidden md:flex items-center border border-gray-700 rounded-md overflow-hidden">
                                <button
                                    onClick={() => {
                                        if (view === 'week') setCurrentWeekDate(addWeeks(currentWeekDate, -1));
                                        else if (view === 'month') setCurrentMonthDate(addMonths(currentMonthDate, -1));
                                        else if (view === 'day') setCurrentDayDate(addDays(currentDayDate, -1));
                                        else setCurrentYearDate(addYears(currentYearDate, -1));
                                    }}
                                    className="p-2 text-gray-300 hover:bg-gray-700"
                                    title={`Previous ${view}`}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => {
                                        if (view === 'week') setCurrentWeekDate(addWeeks(currentWeekDate, 1));
                                        else if (view === 'month') setCurrentMonthDate(addMonths(currentMonthDate, 1));
                                        else if (view === 'day') setCurrentDayDate(addDays(currentDayDate, 1));
                                        else setCurrentYearDate(addYears(currentYearDate, 1));
                                    }}
                                    className="p-2 text-gray-300 hover:bg-gray-700 border-l border-gray-700"
                                    title={`Next ${view}`}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                            {/* Search */}
                            <button
                                onClick={() => setSearchOpen(v => !v)}
                                className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700"
                                aria-label="Search"
                            >
                                <Search className="w-4 h-4" />
                            </button>
                        </div>
                        {/* Follow up shortcut */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    try {
                                        // On mobile (<= 768px), navigate to full Follow up page with a back link target
                                        if (window.innerWidth < 768) {
                                            // Save a return target so Follow up page can add a Back button
                                            sessionStorage.setItem('gv:returnTo', 'calendar');
                                            // Dispatch global navigation to Follow up page
                                            window.dispatchEvent(new CustomEvent('gv:navigate', { detail: { view: 'todo' } } as any));
                                        } else {
                                            setTasksOpen(true);
                                        }
                                    } catch {
                                        setTasksOpen(true);
                                    }
                                }}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700 text-sm"
                                title="Follow up"
                            >
                                <CheckSquare className="w-4 h-4" />
                                <span className="hidden sm:inline">Follow up</span>
                            </button>
                        </div>
                    </div>
                    {/* Expanding search input */}
                    {searchOpen && (
                        <div className="mt-2">
                            <input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search"
                                className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-gray-200 text-sm"
                            />
                        </div>
                    )}

                    {/* Top-half month panel (mobile only) */}
                    {monthPanelOpen && (
                        <div className="mt-3">
                            <div className="md:hidden bg-gray-900 border border-gray-800 rounded-lg p-2 max-h-[50vh] overflow-y-auto">
                                <MiniMonthPicker
                                    date={currentDayDate}
                                    onChange={(d) => {
                                        setCurrentDayDate(d);
                                        if (view === 'week') setCurrentWeekDate(d);
                                        else if (view === 'month') setCurrentMonthDate(new Date(d.getFullYear(), d.getMonth(), 1));
                                        else if (view === 'year') setCurrentYearDate(new Date(d.getFullYear(), 0, 1));
                                        setMonthPanelOpen(false);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Calendar Content */}

                {/* Filter status */}
                {selectedPersonId && (
                    <div className="mb-3 px-3 py-2 bg-blue-900/20 border border-blue-700 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-blue-200">
                                Showing all calendar events with <strong>{personOptions.find(o => o.id === selectedPersonId)?.name}</strong>
                            </div>
                            <button
                                onClick={() => setTimelineModalOpen(true)}
                                className="text-xs text-blue-400 hover:text-blue-300 underline"
                            >
                                View Complete Timeline →
                            </button>
                        </div>
                    </div>
                )}

                {view === 'week' ? (
                    <WeekView
                        currentWeek={currentWeekDate}
                        selectedDay={currentDayDate}
                        items={filteredItems}
                        onWeekChange={setCurrentWeekDate}
                        onEventClick={handleEventClick}
                        onTimeSlotClick={handleTimeSlotClick}
                    />
                ) : view === 'month' ? (
                    <MonthView
                        date={currentMonthDate}
                        items={filteredItems}
                        onDateChange={setCurrentMonthDate}
                        onSelectDay={(d) => setDayModal({ open: true, date: d })}
                        onSelectEvent={handleEventClick}
                        onSwitchToDay={(d) => { setCurrentDayDate(d); setView('day'); }}
                    />
                ) : view === 'day' ? (
                    <DayView
                        date={currentDayDate}
                        items={filteredItems}
                        onAddTime={(hour) => handleTimeSlotClick(currentDayDate, hour)}
                        onSelectEvent={handleEventClick}
                    />
                ) : (
                    <YearView
                        date={currentYearDate}
                        onSelectMonth={(monthIndex) => { setCurrentMonthDate(new Date(currentYearDate.getFullYear(), monthIndex, 1)); setView('month'); }}
                    />
                )}

                {/* Inline event details on mobile */}
                {isMobile && (
                    <EventDetailsModal
                        event={selectedEvent}
                        isOpen={eventModalOpen}
                        onClose={closeEventModal}
                        onSave={handleEventSave}
                        onDelete={handleEventDelete}
                        user={user}
                        defaultDate={eventModalDefaults.date}
                        defaultTime={eventModalDefaults.time}
                        initialReadOnly={eventModalViewOnly}
                        groupId={activeGroupId || null}
                        renderInline={true}
                    />
                )}
            </div>

            {/* Event Details overlay on desktop */}
            {!isMobile && (
                <EventDetailsModal
                    event={selectedEvent}
                    isOpen={eventModalOpen}
                    onClose={closeEventModal}
                    onSave={handleEventSave}
                    onDelete={handleEventDelete}
                    user={user}
                    defaultDate={eventModalDefaults.date}
                    defaultTime={eventModalDefaults.time}
                    initialReadOnly={eventModalViewOnly}
                    groupId={activeGroupId || null}
                    renderInline={false}
                />
            )}

            {/* Transcript View */}
            {transcriptChatOpen && transcriptEvent && (
                <TranscriptView
                    event={transcriptEvent}
                    isOpen={transcriptChatOpen}
                    onClose={() => {
                        setTranscriptChatOpen(false);
                        setTranscriptEvent(null);
                    }}
                    onContactSelect={(contactId: string) => {
                        // Navigate to contact profile
                        setTranscriptChatOpen(false);
                        setTranscriptEvent(null);
                        // You might want to add navigation to contact profile here
                        console.log('Navigate to contact:', contactId);
                    }}
                    currentUserId={user?.id}
                />
            )}

            {/* Day Modal (Month view day drill-down) */}
            {dayModal.open && dayModal.date && (
                <DayViewModal
                    date={dayModal.date}
                    events={filteredItems.filter(it => it.due_date && it.due_date.startsWith(dayModal.date!.toISOString().split('T')[0]))}
                    onClose={() => setDayModal({ open: false, date: null })}
                    onAdd={({ dateISO }) => {
                        // Open quick event modal defaulting to selected day
                        setSelectedEvent(null);
                        setEventModalDefaults({ date: dateISO });
                        setEventModalViewOnly(false);
                        setEventModalOpen(true);
                    }}
                    onSelectEvent={handleEventClick}
                />
            )}

            {/* Mobile Quick Add FAB */}
            <button
                onClick={() => setQuickAddOpen(true)}
                className="md:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-lg bg-blue-600 hover:bg-blue-500 text-white shadow-xl border border-blue-500 flex items-center justify-center"
                aria-label="Quick add"
            >
                <Plus className="w-7 h-7" />
            </button>

            {/* Quick Add Modal */}
            {quickAddOpen && (
                <div className="fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setQuickAddOpen(false)} />
                    <div className="absolute inset-x-0 bottom-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 w-full md:max-w-md bg-gray-900 border border-gray-800 rounded-t-2xl md:rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-white font-medium">Quick Add</div>
                            <button className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800" onClick={() => setQuickAddOpen(false)}>
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2 mb-3 text-xs">
                            <button onClick={() => setQuickAddType('event')} className={`px-2 py-1 rounded border ${quickAddType === 'event' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>Event</button>
                            <button onClick={() => setQuickAddType('task')} className={`px-2 py-1 rounded border ${quickAddType === 'task' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>Task</button>
                        </div>
                        <div className="space-y-3">
                            <input
                                value={quickAddTitle}
                                onChange={(e) => setQuickAddTitle(e.target.value)}
                                placeholder={quickAddType === 'task' ? 'Task title' : 'Event title'}
                                className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-gray-200 text-sm"
                                autoFocus
                            />
                            <div className="flex items-center gap-2">
                                <input type="date" value={quickAddDate} onChange={(e) => setQuickAddDate(e.target.value)} className="bg-gray-950 border border-gray-800 rounded text-gray-200 px-2 py-1 text-sm" />
                                <label className="flex items-center gap-2 text-xs text-gray-300">
                                    <input type="checkbox" checked={quickAddAllDay} onChange={(e) => setQuickAddAllDay(e.target.checked)} /> All day
                                </label>
                                {!quickAddAllDay && (
                                    <input type="time" value={quickAddTime} onChange={(e) => setQuickAddTime(e.target.value)} className="bg-gray-950 border border-gray-800 rounded text-gray-200 px-2 py-1 text-sm" />
                                )}
                            </div>
                            <div className="flex items-center justify-end gap-2">
                                <button onClick={() => setQuickAddOpen(false)} className="px-3 py-1.5 rounded border border-gray-700 text-gray-300 text-sm">Cancel</button>
                                <button onClick={saveQuickAdd} disabled={!quickAddTitle.trim() || quickAddSaving} className="px-3 py-1.5 rounded bg-blue-600 border border-blue-500 text-white text-sm disabled:opacity-50">
                                    {quickAddSaving ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Contact-First Event Modal */}
            <ContactFirstEventModal
                isOpen={contactFirstModalOpen}
                onClose={() => setContactFirstModalOpen(false)}
                onEventCreated={(event) => {
                    setItems(prev => [...prev, event]);
                    setContactFirstModalOpen(false);
                }}
                user={user}
            />

            {/* Contact Timeline Modal */}
            {selectedPersonId && (
                <ContactTimelineModal
                    contactId={selectedPersonId}
                    contactName={personOptions.find(o => o.id === selectedPersonId)?.name || 'Contact'}
                    isOpen={timelineModalOpen}
                    onClose={() => setTimelineModalOpen(false)}
                    onEventClick={(eventId) => {
                        // Find and open the event
                        const event = items.find(item => item.id === eventId);
                        if (event) {
                            // Check if this is a transcript event by looking for transcript markers in title or notes
                            const isTranscript = event.title?.toLowerCase().includes('transcript') ||
                                event.title?.toLowerCase().includes('call transcript') ||
                                (event.notes && event.notes.includes(':')) && // Contains speaker format like "Name: text"
                                (event.notes.split('\n').some(line => /^[^:]+:\s*/.test(line.trim())));

                            if (isTranscript) {
                                // Open transcript chat view
                                setTranscriptEvent(event);
                                setTranscriptChatOpen(true);
                            } else {
                                // Open regular event modal in view-only mode first
                                setSelectedEvent(event);
                                setEventModalViewOnly(true);
                                setEventModalOpen(true);
                            }
                        }
                    }}
                />
            )}

            {/* Undo Toast */}
            {pendingUndo && (
                <UndoToast
                    message={pendingUndo.message}
                    secondsRemaining={undoSeconds}
                    onUndo={async () => {
                        await pendingUndo.onUndo();
                        setPendingUndo(null);
                    }}
                    onClose={() => setPendingUndo(null)}
                />
            )}

            {/* Mobile Menu Drawer (left) */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div className={`absolute inset-0 bg-black transition-opacity duration-200 ${mobileMenuSlide ? 'bg-opacity-60' : 'bg-opacity-0'}`} onClick={closeMobileMenuAnimated} />
                    <div className={`absolute left-0 top-0 h-full w-80 bg-gray-900 border-r border-gray-800 p-4 overflow-y-auto no-scrollbar transform transition-transform duration-200 ${mobileMenuSlide ? 'translate-x-0' : '-translate-x-full'}`}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-white font-medium">Calendar Menu</div>
                            <button className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800" onClick={closeMobileMenuAnimated}>
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* View selection moved here for mobile */}
                            <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
                                <div className="text-xs text-gray-400 mb-2">View</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button onClick={() => { setView('day'); setMobileMenuOpen(false); }} className={`px-2 py-1 rounded border text-sm ${view === 'day' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>Day</button>
                                    <button onClick={() => { setView('week'); setMobileMenuOpen(false); }} className={`px-2 py-1 rounded border text-sm ${view === 'week' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>Week</button>
                                    <button onClick={() => { setView('month'); setMobileMenuOpen(false); }} className={`px-2 py-1 rounded border text-sm ${view === 'month' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>Month</button>
                                    <button onClick={() => { setView('year'); setMobileMenuOpen(false); }} className={`px-2 py-1 rounded border text-sm ${view === 'year' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>Year</button>
                                </div>
                            </div>
                            <MiniMonthPicker
                                date={currentDayDate}
                                onChange={(d) => {
                                    setCurrentDayDate(d);
                                    if (view === 'week') setCurrentWeekDate(d);
                                    else if (view === 'month') setCurrentMonthDate(new Date(d.getFullYear(), d.getMonth(), 1));
                                    else if (view === 'year') setCurrentYearDate(new Date(d.getFullYear(), 0, 1));
                                    closeMobileMenuAnimated();
                                }}
                            />

                            <CalendarManager
                                user={user}
                                selectedCalendars={selectedCalendars}
                                onCalendarToggle={handleCalendarToggle}
                                onCalendarsChange={setCalendars}
                            />

                            <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-xl p-4 space-y-3 shadow-lg">
                                <div className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-3">Quick Actions</div>

                                <button
                                    onClick={() => { openContactFirstModal(); closeMobileMenuAnimated(); }}
                                    className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-4 py-3 rounded-lg shadow-md hover:shadow-lg font-medium transition-all duration-200"
                                >
                                    <UserIcon className="w-5 h-5" />
                                    <span>Quick Event</span>
                                </button>

                                <button
                                    onClick={() => { openNewEventModal(); closeMobileMenuAnimated(); }}
                                    className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 text-gray-100 px-4 py-3 rounded-lg shadow-md hover:shadow-lg font-medium transition-all duration-200 border border-gray-600"
                                >
                                    <Plus className="w-5 h-5" />
                                    <span>New Event</span>
                                </button>
                            </div>

                            {/* Filters removed per product direction */}
                        </div>
                    </div>
                </div>
            )}

            {/* Tasks Drawer (right) */}
            {tasksOpen && (
                <div className="fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setTasksOpen(false)} />
                    <div className="absolute right-0 top-0 h-full w-96 max-w-full bg-gray-900 border-l border-gray-800 p-4 overflow-y-auto no-scrollbar">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-white font-medium">
                                <CheckSquare className="w-4 h-4" /> Follow ups
                            </div>
                            <button className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800" onClick={() => setTasksOpen(false)}>
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm text-gray-300">Your open follow ups</div>
                            <button
                                onClick={async () => {
                                    try {
                                        setTasksLoading(true);
                                        const rows = await listTodos(user.id, { includeCompleted: false, groupId: activeGroupId || null });
                                        setTasks(rows);
                                    } finally {
                                        setTasksLoading(false);
                                    }
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 border border-gray-700 rounded text-gray-300 hover:bg-gray-800 text-xs"
                                title="Refresh"
                            >
                                <RefreshCcw className="w-3 h-3" /> Refresh
                            </button>
                        </div>

                        {tasksLoading ? (
                            <div className="text-xs text-gray-400">Loading…</div>
                        ) : tasks.length === 0 ? (
                            <div className="text-xs text-gray-400">No open follow ups.</div>
                        ) : (
                            (() => {
                                // Group tasks by local date (YYYY-MM-DD)
                                const byDate = tasks
                                    .slice()
                                    .sort((a, b) => new Date(a.due_date || 0).getTime() - new Date(b.due_date || 0).getTime())
                                    .reduce<Record<string, TodoItem[]>>((acc, t) => {
                                        const key = t.due_date ? new Date(t.due_date).toISOString().slice(0, 10) : 'no-date';
                                        (acc[key] ||= []).push(t);
                                        return acc;
                                    }, {});
                                const keys = Object.keys(byDate);
                                return (
                                    <div className="space-y-4">
                                        {keys.map(k => (
                                            <div key={k}>
                                                <div className="text-xs font-semibold text-gray-300 mb-2">
                                                    {k === 'no-date' ? 'No date' : new Date(k + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                </div>
                                                <ul className="space-y-2">
                                                    {byDate[k].map(t => (
                                                        <li key={t.id} className="flex items-start gap-2 p-2 bg-gray-800/50 rounded border border-gray-800">
                                                            <input
                                                                type="checkbox"
                                                                className="mt-0.5 accent-blue-600"
                                                                onChange={async (e) => {
                                                                    const checked = e.currentTarget.checked;
                                                                    try {
                                                                        await updateTodo(t.id, { done: checked });
                                                                        setTasks(prev => prev.filter(x => x.id !== t.id));
                                                                    } catch { }
                                                                }}
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm text-gray-200 truncate">{t.title}</div>
                                                                {t.due_date && (
                                                                    <div className="text-[11px] text-gray-400">{new Date(t.due_date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}